import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useFormContext } from 'react-hook-form';
import { FormSchemaCreate } from './type';
import { Button } from '@/components/ui/button';
import {
  ArrowTopRightIcon,
  ExclamationTriangleIcon,
} from '@radix-ui/react-icons';
import { Link } from 'react-router-dom';
import { PATH_DOCS, PATH_EXTERNAL } from '@/constants';
import HugginFaceIcon from '@/assets/image/huggingface_logo-noborder.svg';
import { useSendCheckHuggingFaceSupport } from '@/service/one-click-deploy/hook';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  CheckHuggingFaceSupportCode,
  CheckHuggingFaceSupportResponse,
  OptimizationTechnique,
} from '@/service/one-click-deploy/type';
import { toastSomethingError } from '@/components/ui/toast-content';

type Props = {
  onNextStep: (e: CheckHuggingFaceSupportResponse) => void;
};
const FormHugginFace = (props: Props) => {
  const { onNextStep } = props;

  const sendCheckHuggingFaceSupport = useSendCheckHuggingFaceSupport({
    onError: () => {
      toastSomethingError();
    },
  });

  const form = useFormContext<FormSchemaCreate>();

  const handleCheckHuggingFaceSupport = async () => {
    const isValid = await form.trigger(['huggingFace.link']);
    if (!isValid) return;
    const data = await sendCheckHuggingFaceSupport.mutateAsync({
      link: form.getValues('huggingFace').link,
      token: form.getValues('huggingFace').token || undefined,
    });
    if (data?.code === CheckHuggingFaceSupportCode.ModelSupported) {
      const repoName = form.getValues('huggingFace').link;
      form.setValue('model.modelName', repoName);
      form.setValue('model.minVRamRequiredGB', data?.min_vram_required_GB);
      form.setValue('model.architecture', data?.model_architecture);
      if (!(data?.model_architecture === 'Attention')) {
        form.setValue(
          'instance.optimizationTechnique',
          OptimizationTechnique.No
        );
      }
      onNextStep(data);
    }
    // form.setValue('model.modelName', 'google');
    // form.setValue('model.minVRamRequiredGB', 24);
    // onNextStep(data);
  };

  const RenderError = () => {
    if (!sendCheckHuggingFaceSupport.data?.code) return <></>;
    switch (sendCheckHuggingFaceSupport.data?.code) {
      case CheckHuggingFaceSupportCode.ModelSupported:
        return <></>;
      case CheckHuggingFaceSupportCode.ModelArchitectureNotSupported:
        return (
          <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Not Support</AlertTitle>
            <AlertDescription>
              {sendCheckHuggingFaceSupport.data?.code}
              <br />
              <Link
                to={PATH_DOCS.validatedModel}
                className="underline"
                rel="noopener noreferrer"
                target="_blank"
              >
                Explore available model support.
              </Link>
            </AlertDescription>
          </Alert>
        );
      case CheckHuggingFaceSupportCode.ModelRepoNotAuthenticated:
        return (
          <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Not Authenticated</AlertTitle>
            <AlertDescription>
              {sendCheckHuggingFaceSupport.data?.code}
              <br />
              Please enter your Hugging Face Token.
            </AlertDescription>
          </Alert>
        );
      default:
        return (
          <Alert variant="destructive">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {sendCheckHuggingFaceSupport.data?.code}
            </AlertDescription>
          </Alert>
        );
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <img src={HugginFaceIcon} className="h-5 w-5" />
            <CardTitle>Hugging Face</CardTitle>
            <Link
              to={PATH_EXTERNAL.huggingFaceModel}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowTopRightIcon />
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid w-full gap-2">
            <FormField
              control={form.control}
              name="huggingFace.link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Model Repository</FormLabel>
                  <FormDescription className="!mt-0">
                    Import a model from the Hugging Face hub.
                  </FormDescription>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. SeaLLMs/SeaLLMs-v3-1.5B-Chat"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="huggingFace.token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hugging Face Token (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. hf_ABcdeFGHijKLMNopqlgwuYFXqETatJvzIx"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>
      <RenderError />
      <Button
        type="button"
        onClick={handleCheckHuggingFaceSupport}
        disabled={sendCheckHuggingFaceSupport.isPending}
      >
        Next
      </Button>
    </>
  );
};

export default FormHugginFace;
